import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { getConfig } from '../config';
import { loadGoogleIdentity } from '@services/googleIdentity';
import { Button } from './Button';
import { Alert } from './Dialog';

/**
 * Tela de login — Google (Google Identity Services; o cadastro é criado no primeiro acesso, com
 * período de teste) ou Entra ID (Microsoft, só usuários atribuídos ao aplicativo). Não existe senha
 * local. O botão do Google é desenhado pelo próprio GIS dentro do container `googleRef`.
 */
export const Login: React.FC = () => {
  const { login, loginWithGoogle, isLoading, error, clearError, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const googleRef = useRef<HTMLDivElement>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  const googleClientId = (() => {
    try {
      return getConfig().auth.google?.clientId?.trim() || '';
    } catch {
      return '';
    }
  })();

  // Já autenticado → volta para onde estava (ou Hoje).
  useEffect(() => {
    if (user && !isLoading) {
      const from = location.state?.from || '/';
      navigate(from, { replace: true });
    }
  }, [user, isLoading, navigate, location]);

  // Carrega o GIS e desenha o botão oficial do Google (largura do container, no máximo 400 px).
  useEffect(() => {
    if (!googleClientId) return;
    let cancelled = false;

    loadGoogleIdentity()
      .then((google) => {
        if (cancelled || !googleRef.current) return;
        google.initialize({
          client_id: googleClientId,
          callback: (response) => {
            clearError();
            setGoogleError(null);
            loginWithGoogle(response.credential).catch((err) => {
              console.error('Google login error:', err);
            });
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin',
          ux_mode: 'popup',
          itp_support: true,
        });
        const width = Math.min(400, Math.max(200, Math.floor(googleRef.current.offsetWidth || 320)));
        googleRef.current.innerHTML = '';
        google.renderButton(googleRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          locale: 'pt-BR',
          width,
        });
        setGoogleReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('Google Identity Services indisponível:', err);
        setGoogleError('Não foi possível carregar o login com Google. Verifique bloqueadores de conteúdo ou use a conta Microsoft.');
      });

    return () => {
      cancelled = true;
    };
    // O botão é desenhado uma vez por carga da tela; clearError/loginWithGoogle são estáveis o bastante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleClientId]);

  const handleAzureLogin = async () => {
    try {
      clearError();
      await login();
    } catch (err) {
      console.error('Azure AD login error:', err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center gradient-login-bg p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-card p-6 sm:p-8 animate-slide-up">
          <div className="text-center mb-8">
            <img src="/plan_logo.svg" alt="Plan" className="w-16 h-16 rounded-xl mx-auto mb-4" />
            <h1 className="text-h2">Plan — Giannotti</h1>
            <p className="text-slate-500 mt-2">Agenda, finanças e documentos pessoais</p>
          </div>

          {error && (
            <Alert variant="error" className="mb-6" onClose={clearError}>
              {error}
            </Alert>
          )}
          {googleError && (
            <Alert variant="warning" className="mb-6" onClose={() => setGoogleError(null)}>
              {googleError}
            </Alert>
          )}

          <div className="space-y-4 mb-6">
            {googleClientId && (
              <div className="space-y-2">
                {/* Container do botão oficial do Google (google.accounts.id.renderButton). */}
                <div className="relative flex justify-center min-h-[44px]">
                  <div ref={googleRef} aria-label="Entrar com Google" className="w-full flex justify-center" />
                  {!googleReady && !googleError && (
                    <div className="absolute inset-0 flex justify-center" aria-hidden="true">
                      <div className="h-10 w-full max-w-[400px] rounded-md border border-slate-200 animate-pulse" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 text-center px-2">
                  Novo por aqui? Entre com o Google — o cadastro é criado na hora, com período de teste gratuito.
                </p>
              </div>
            )}

            {googleClientId && (
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span className="flex-1 border-t border-slate-200" />
                ou
                <span className="flex-1 border-t border-slate-200" />
              </div>
            )}

            <Button
              variant="primary"
              onClick={handleAzureLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2"
            >
              <Shield size={20} />
              {isLoading ? 'Conectando...' : 'Entrar com a conta Microsoft'}
            </Button>

            <p className="text-xs text-gray-500 text-center px-2">
              Se a tela da Microsoft ou do Google não abrir, verifique se os popups estão bloqueados no seu navegador.
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-small text-slate-500">
              Ao entrar você concorda com os{' '}
              <Link to="/termos" className="text-emerald-700 hover:underline">
                Termos de uso
              </Link>{' '}
              e a{' '}
              <Link to="/privacidade" className="text-emerald-700 hover:underline">
                Política de privacidade
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
